import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo } from 'react';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { getUrl } from '@atlaskit/linking-common';
import { context, invokeFailedEvent as _invokeFailedEvent, invokeSucceededEvent as _invokeSucceededEvent, uiActionClickedEvent, uiRenderFailedEvent, uiRenderSuccessEvent } from '../../utils/analytics';
import { getDefinitionId, getExtensionKey, getProduct, getResourceType, getStatusDetails, getSubproduct } from '../helpers';
import { failUfoExperience, startUfoExperience, succeedUfoExperience } from './ufoExperiences';
import { useDispatchAnalytics } from './useDispatchAnalytics';
var applyCommonAttributes = function applyCommonAttributes(event, commonAttributes) {
  if (event && event.attributes) {
    for (var _i = 0, _Object$entries = Object.entries(commonAttributes); _i < _Object$entries.length; _i++) {
      var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
        key = _Object$entries$_i[0],
        value = _Object$entries$_i[1];
      if (event.attributes[key] === undefined) {
        event.attributes[key] = value;
      }
    }
  }
  return event;
};

/**
 * This hook provides usage of Smart Link analytics outside of the Card component.
 * Can be provided to Card via the analyticsEvents prop to change the analytics events.
 *
 * @param url URL of the link
 * @param id fallback id of the events sent if no id is available
 * @param defaultLocation location attribute to be used
 * @returns
 */
export var useSmartLinkAnalytics = function useSmartLinkAnalytics(url, id, defaultLocation) {
  var _useDispatchAnalytics = useDispatchAnalytics(),
    dispatchAnalytics = _useDispatchAnalytics.dispatchAnalytics;
  var defaultId = id || 'NULL';
  // We don't want to trigger a re-render by using useSmartCardState
  var _useSmartLinkContext = useSmartLinkContext(),
    store = _useSmartLinkContext.store;
  var state = store ? getUrl(store, url) : undefined;
  var details = state ? state.details : undefined;
  var extractedDefinitionId = getDefinitionId(details);
  var extractedExtensionKey = getExtensionKey(details);
  var extractedResourceType = getResourceType(details);
  var extractedSubproduct = getSubproduct(details);
  var extractedProduct = getProduct(details);
  var extractedStatusDetails = getStatusDetails(details);
  var commonAttributes = useMemo(function () {
    return {
      id: defaultId,
      definitionId: extractedDefinitionId,
      extensionKey: extractedExtensionKey,
      resourceType: extractedResourceType,
      destinationObjectType: extractedResourceType,
      destinationSubproduct: extractedSubproduct,
      destinationProduct: extractedProduct,
      location: defaultLocation,
      statusDetails: extractedStatusDetails
    };
  }, [defaultId, extractedDefinitionId, extractedExtensionKey, extractedResourceType, extractedSubproduct, extractedProduct, defaultLocation, extractedStatusDetails]);

  /** Contains all ui analytics events */
  var ui = useMemo(function () {
    return {
      /**
       * This fires an event that represents when a user
       * click a button.
       * @param data A partial analytics event payload
       * @deprecated consider removing when cleaning up FF platform-smart-card-migrate-embed-modal-analytics
       */
      buttonClickedEvent: function buttonClickedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'clicked',
          actionSubject: 'button',
          actionSubjectId: data.actionSubjectId,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'ui'
        }, commonAttributes));
      },
      /**
       * This fires an event that represents when a user clicks on a Smart Link action.
       * Note: This also starts the UFO smart-link-action-invocation experience.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of the action that was clicked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @returns
       */
      actionClickedEvent: function actionClickedEvent(_ref) {
        var id = _ref.id,
          actionType = _ref.actionType,
          display = _ref.display,
          overrideExtensionKey = _ref.extensionKey,
          definitionId = _ref.definitionId,
          resourceType = _ref.resourceType,
          destinationProduct = _ref.destinationProduct,
          destinationSubproduct = _ref.destinationSubproduct,
          location = _ref.location;
        var extensionKey = overrideExtensionKey !== null && overrideExtensionKey !== void 0 ? overrideExtensionKey : extractedExtensionKey;
        dispatchAnalytics(applyCommonAttributes(uiActionClickedEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a user close a modal.
       * @param data A partial analytics event payload
       * @deprecated consider removing when cleaning up FF platform-smart-card-migrate-embed-modal-analytics
       */
      modalClosedEvent: function modalClosedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'closed',
          actionSubject: 'modal',
          actionSubjectId: data.actionSubjectId,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'ui'
        }, commonAttributes));
      },
      /**
       * This fires an event that represents when a Smart Link was rendered successfully.
       * Note: this fires even if the Smart Link request errored out.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param id The unique ID for this Smart Link.
       * @param definitionId The definitionId of the Smart Link resolver invoked.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param canBeDatasource An indicator that shows that a smart link can be converted to a datasource
       * @deprecated consider removing when cleaning up FF platform-smart-card-migrate-embed-modal-analytics
       */
      renderSuccessEvent: function renderSuccessEvent(_ref2) {
        var display = _ref2.display,
          status = _ref2.status,
          id = _ref2.id,
          extensionKey = _ref2.extensionKey,
          definitionId = _ref2.definitionId,
          resourceType = _ref2.resourceType,
          destinationProduct = _ref2.destinationProduct,
          destinationSubproduct = _ref2.destinationSubproduct,
          location = _ref2.location,
          _ref2$canBeDatasource = _ref2.canBeDatasource,
          canBeDatasource = _ref2$canBeDatasource === void 0 ? false : _ref2$canBeDatasource;
        var experienceId = id ? id : defaultId;
        succeedUfoExperience('smart-link-rendered', experienceId, {
          extensionKey: extensionKey,
          display: display
        });

        // UFO will disregard this if authentication experience has not yet been started
        succeedUfoExperience('smart-link-authenticated', experienceId, {
          display: display
        });
        dispatchAnalytics(applyCommonAttributes(uiRenderSuccessEvent({
          display: display,
          status: status,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location,
          canBeDatasource: canBeDatasource
        }), commonAttributes));
      },
      /**
       * This fires an event that represents when a Smart Link renders unsuccessfuly.
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param id The unique ID for this Smart Link.
       * @param error: An error representing why the Smart Link render failed.
       * @param errorInfo: Additional details about the error including the stack trace.
       * @deprecated consider removing when cleaning up FF platform-smart-card-migrate-embed-modal-analytics
       */
      renderFailedEvent: function renderFailedEvent(_ref3) {
        var display = _ref3.display,
          id = _ref3.id,
          error = _ref3.error,
          errorInfo = _ref3.errorInfo,
          extensionKey = _ref3.extensionKey,
          definitionId = _ref3.definitionId,
          resourceType = _ref3.resourceType,
          destinationProduct = _ref3.destinationProduct,
          destinationSubproduct = _ref3.destinationSubproduct,
          location = _ref3.location;
        var experienceId = id ? id : defaultId;
        // Start and fail the smart-link-rendered experience. If it has already
        // been started nothing happens.
        startUfoExperience('smart-link-rendered', experienceId);
        failUfoExperience('smart-link-rendered', experienceId);
        failUfoExperience('smart-link-authenticated', experienceId);
        dispatchAnalytics(applyCommonAttributes(uiRenderFailedEvent({
          display: display,
          error: error,
          errorInfo: errorInfo,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      }
    };
  }, [dispatchAnalytics, commonAttributes, defaultId, extractedExtensionKey]);

  /** Contains all operational analytics events */
  var operational = useMemo(function () {
    return {
      /**
       * This fires an event that represents an action being successfully invoked.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of action invoked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       */
      invokeSucceededEvent: function invokeSucceededEvent(_ref4) {
        var id = _ref4.id,
          actionType = _ref4.actionType,
          display = _ref4.display,
          extensionKey = _ref4.extensionKey,
          definitionId = _ref4.definitionId,
          resourceType = _ref4.resourceType,
          destinationProduct = _ref4.destinationProduct,
          destinationSubproduct = _ref4.destinationSubproduct,
          location = _ref4.location;
        dispatchAnalytics(applyCommonAttributes(_invokeSucceededEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      },
      /**
       * This fires an event that represents an action being unsuccessfully invoked.
       * @param id The unique ID for this Smart Link.
       * @param extensionKey The extensionKey of the Smart Link resovler invoked.
       * @param actionType The type of action invoked, e.g. PreviewAction
       * @param display Whether the card was an Inline, Block, Embed or Flexible UI.
       * @param reason The reason the invocation failed.
       */
      invokeFailedEvent: function invokeFailedEvent(_ref5) {
        var id = _ref5.id,
          actionType = _ref5.actionType,
          display = _ref5.display,
          reason = _ref5.reason,
          extensionKey = _ref5.extensionKey,
          definitionId = _ref5.definitionId,
          resourceType = _ref5.resourceType,
          destinationProduct = _ref5.destinationProduct,
          destinationSubproduct = _ref5.destinationSubproduct,
          location = _ref5.location;
        dispatchAnalytics(applyCommonAttributes(_invokeFailedEvent({
          id: id !== null && id !== void 0 ? id : defaultId,
          actionType: actionType,
          display: display,
          reason: reason,
          extensionKey: extensionKey,
          definitionId: definitionId,
          resourceType: resourceType,
          destinationProduct: destinationProduct,
          destinationSubproduct: destinationSubproduct,
          location: location
        }), commonAttributes));
      }
    };
  }, [defaultId, commonAttributes, dispatchAnalytics]);

  /** Contains all screen analytics events */
  var screen = useMemo(function () {
    return {
      /**
       * This fires an event that represents when a user view a modal.
       * @deprecated consider removing when cleaning up FF platform-smart-card-migrate-embed-modal-analytics
       */
      modalViewedEvent: function modalViewedEvent(data) {
        return dispatchAnalytics(applyCommonAttributes({
          action: 'viewed',
          actionSubject: data.name,
          attributes: _objectSpread(_objectSpread({}, context), data.attributes),
          eventType: 'screen',
          name: data.name
        }, commonAttributes));
      }
    };
  }, [commonAttributes, dispatchAnalytics]);
  return useMemo(function () {
    return {
      ui: ui,
      operational: operational,
      screen: screen
    };
  }, [ui, operational, screen]);
};